import { Color3, type Color4, type GlowLayer, Mesh, MeshBuilder, type Scene, StandardMaterial, VertexBuffer, VertexData } from "@babylonjs/core";

/**
 * A gradient dome that follows the camera: `top` overhead fading to `horizon` (and below). Sky
 * meshes are unlit, so they stay out of the glow layer (or they'd bloom over the whole frame).
 */
export function skyDome(scene: Scene, glow: GlowLayer, top: string, horizon: string, radius = 360): Mesh {
  const dome = MeshBuilder.CreateSphere("skyDome", { diameter: radius * 2, segments: 24, sideOrientation: Mesh.BACKSIDE }, scene);
  const pos = dome.getVerticesData(VertexBuffer.PositionKind)!;
  const a = Color3.FromHexString(horizon);
  const b = Color3.FromHexString(top);
  const colors: number[] = [];
  for (let i = 0; i < pos.length; i += 3) {
    // Ease in: the horizon band stays pale, the colour gathers overhead.
    const k = Math.max(0, pos[i + 1]! / radius) ** 0.7;
    colors.push(a.r + (b.r - a.r) * k, a.g + (b.g - a.g) * k, a.b + (b.b - a.b) * k, 1);
  }
  dome.setVerticesData(VertexBuffer.ColorKind, colors);
  dome.material = unlit(scene, "skyDome");
  dome.infiniteDistance = true;
  dome.applyFog = false;
  dome.isPickable = false;
  glow.addExcludedMesh(dome);
  return dome;
}

function unlit(scene: Scene, name: string) {
  const m = new StandardMaterial(name, scene);
  m.emissiveColor = Color3.White();
  m.diffuseColor = Color3.Black();
  m.specularColor = Color3.Black();
  m.disableLighting = true;
  return m;
}

/**
 * Comic cumulus: flat-bottomed heaps of puffs, flat white with a pale shade underneath and an ink
 * contour, strewn in a ring around the map (or round cx, cz). Deterministic, so every client sees the
 * same sky.
 */
export function inkedClouds(
  scene: Scene,
  glow: GlowLayer,
  ink: Color4,
  opts: { count?: number; near?: number; far?: number; low?: number; high?: number; seed?: number; shade?: string; white?: string; cx?: number; cz?: number } = {},
): Mesh[] {
  const { count = 14, near = 210, far = 320, low = 55, high = 115, seed = 7, shade = "#d9e5ec", white: top = "#fffdf6", cx: ox = 0, cz: oz = 0 } = opts;
  let s = seed >>> 0;
  const rnd = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
  const white = Color3.FromHexString(top);
  const under = Color3.FromHexString(shade);
  const mat = unlit(scene, "clouds");
  const out: Mesh[] = [];
  for (let c = 0; c < count; c++) {
    const ang = (c / count) * Math.PI * 2 + rnd() * 0.3;
    const dist = near + rnd() * (far - near);
    const cx = ox + Math.cos(ang) * dist;
    const cz = oz + Math.sin(ang) * dist;
    const base = low + rnd() * (high - low);
    const width = 26 + rnd() * 34;
    const puffs = 4 + Math.floor(rnd() * 4);
    const parts: Mesh[] = [];
    // Puffs along a line tangent to the ring, biggest in the middle, all sitting on one base.
    const tx = -Math.sin(ang);
    const tz = Math.cos(ang);
    for (let p = 0; p < puffs; p++) {
      const f = puffs === 1 ? 0.5 : p / (puffs - 1);
      const r = (width / puffs) * (0.9 + Math.sin(f * Math.PI) * 1.1) * (0.8 + rnd() * 0.4);
      const along = (f - 0.5) * width;
      const puff = MeshBuilder.CreateSphere(`puff${c}_${p}`, { diameter: r * 2, segments: 8 }, scene);
      puff.position.set(cx + tx * along, base + r * 0.55, cz + tz * along);
      puff.scaling.y = 0.82;
      parts.push(puff);
    }
    const cloud = Mesh.MergeMeshes(parts, true, true)!;
    cloud.name = `cloud${c}`;
    // Flatten the bottom (comic clouds sit on a level base) and shade the lower third.
    const pos = cloud.getVerticesData(VertexBuffer.PositionKind)!;
    const colors: number[] = [];
    let top = base;
    for (let i = 1; i < pos.length; i += 3) top = Math.max(top, pos[i]!);
    for (let i = 0; i < pos.length; i += 3) {
      if (pos[i + 1]! < base) pos[i + 1] = base;
      const k = (pos[i + 1]! - base) / Math.max(1, top - base) < 0.28 ? 1 : 0;
      const col = k ? under : white;
      colors.push(col.r, col.g, col.b, 1);
    }
    cloud.setVerticesData(VertexBuffer.PositionKind, pos);
    cloud.setVerticesData(VertexBuffer.ColorKind, colors);
    const normals: number[] = [];
    VertexData.ComputeNormals(pos, cloud.getIndices()!, normals);
    cloud.setVerticesData(VertexBuffer.NormalKind, normals);
    cloud.material = mat;
    cloud.applyFog = false;
    cloud.isPickable = false;
    cloud.renderOutline = true;
    cloud.outlineColor = new Color3(ink.r, ink.g, ink.b);
    cloud.outlineWidth = dist * 0.0045;
    cloud.freezeWorldMatrix();
    glow.addExcludedMesh(cloud);
    out.push(cloud);
  }
  return out;
}

/**
 * A towering thunderhead, Florida's summer afternoon: a column of heaped puffs over a dark base,
 * spreading at the top into a flat anvil, inked like the cumulus. Unlit, out of the fog.
 */
export function thunderhead(scene: Scene, glow: GlowLayer, ink: Color4, opts: { x: number; z: number; base?: number; top?: number; width?: number; seed?: number; shade?: string }): Mesh {
  const { x, z, base = 40, top = 200, width = 150, seed = 3, shade = "#aebccb" } = opts;
  let s = seed >>> 0;
  const rnd = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
  const parts: Mesh[] = [];
  const puff = (px: number, py: number, pz: number, r: number, sy = 0.85) => {
    const m = MeshBuilder.CreateSphere(`thunder${parts.length}`, { diameter: r * 2, segments: 8 }, scene);
    m.position.set(px, py, pz);
    m.scaling.y = sy;
    parts.push(m);
  };
  // The column: heaps of puffs, broad at the foot and narrowing a little as they tower up.
  const tiers = 8;
  for (let t = 0; t < tiers; t++) {
    const f = t / (tiers - 1);
    const y = base + (top - base) * 0.8 * f;
    const w = width * (0.62 - 0.26 * f);
    const n = 7 - Math.floor(f * 3);
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2 + rnd() * 0.8;
      puff(x + Math.cos(a) * w * 0.42, y + rnd() * 8, z + Math.sin(a) * w * 0.42, w * (0.42 + rnd() * 0.18));
    }
  }
  // The anvil: wide and flat on top, spreading far downwind.
  for (let k = 0; k < 11; k++) {
    const f = k / 10 - 0.35;
    puff(x + f * width * 2.2, top - rnd() * 10, z + (rnd() - 0.5) * width * 0.6, width * (0.3 + rnd() * 0.1), 0.26);
  }
  const cloud = Mesh.MergeMeshes(parts, true, true)!;
  cloud.name = "thunderhead";
  const pos = cloud.getVerticesData(VertexBuffer.PositionKind)!;
  const colors: number[] = [];
  const white = Color3.FromHexString("#fbfbf6");
  const under = Color3.FromHexString(shade);
  for (let i = 0; i < pos.length; i += 3) {
    if (pos[i + 1]! < base) pos[i + 1] = base;
    const k = (pos[i + 1]! - base) / (top - base);
    const col = k < 0.22 ? under : Color3.Lerp(under, white, Math.min(1, (k - 0.22) * 6));
    colors.push(col.r, col.g, col.b, 1);
  }
  cloud.setVerticesData(VertexBuffer.PositionKind, pos);
  cloud.setVerticesData(VertexBuffer.ColorKind, colors);
  const normals: number[] = [];
  VertexData.ComputeNormals(pos, cloud.getIndices()!, normals);
  cloud.setVerticesData(VertexBuffer.NormalKind, normals);
  cloud.material = unlit(scene, "thunderhead");
  cloud.applyFog = false;
  cloud.isPickable = false;
  cloud.renderOutline = true;
  cloud.outlineColor = new Color3(ink.r, ink.g, ink.b);
  cloud.outlineWidth = 1.4;
  cloud.freezeWorldMatrix();
  glow.addExcludedMesh(cloud);
  return cloud;
}
